// src/pages/Ventas.js
//
// Módulo Ventas — consulta e historial de ventas registradas.
//
// Consume el backend heredado VentasController (/api/pos/ventas):
//   - GET /             → PagedResult (data planos con campos de Venta)
//   - GET /{id}         → VentaDto con Detalles + Pagos
//   - POST /{id}/anular → { motivo, autorizadoPorId? } — motivo ≥ 10 chars
//
// Filtros soportados por el backend: sucursalId, cajaId, aperturaCajaId,
// estado, desde, hasta, page, pageSize. NO soporta busqueda por texto
// libre (se filtra client-side).
//
// El REGISTRO de ventas nuevas se hace desde /pos (pantalla operativa).
// Este módulo es para consulta, auditoría y anulaciones.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, Stack, Button, IconButton, Chip, Alert, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, MenuItem, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Grid, Tabs, Tab, LinearProgress, Paper,
} from '@mui/material';
import ReceiptLongIcon    from '@mui/icons-material/ReceiptLong';
import VisibilityIcon     from '@mui/icons-material/Visibility';
import CancelIcon         from '@mui/icons-material/Cancel';
import RefreshIcon        from '@mui/icons-material/Refresh';
import PointOfSaleIcon    from '@mui/icons-material/PointOfSale';
import StorefrontIcon     from '@mui/icons-material/Storefront';
import LocalShippingIcon  from '@mui/icons-material/LocalShipping';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ventas as apiVentas, sucursales as apiSucursales } from '../api/posApi';

const fmtCOP = (n) => (Number(n) || 0).toLocaleString('es-CO',
  { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const fmtFechaHora = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('es-CO',
      { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
};
const hoyISO   = () => new Date().toISOString().slice(0, 10);
const ayerISO  = () => new Date(Date.now() - 86400000).toISOString().slice(0, 10);

const colorEstadoPago = (ep) =>
  ep === 'PAGADO' ? 'success' : ep === 'PARCIAL' ? 'info' : ep === 'PENDIENTE' ? 'warning' : 'default';

const Ventas = () => {
  const { empresa, sucursalActivaId, notify } = useApp();
  const navigate = useNavigate();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  const [sucursales, setSucursales] = useState([]);
  const [filtros, setFiltros] = useState({
    sucursalId: sucursalActivaId || '',
    estado: '', estadoPago: '',            // estadoPago se aplica client-side
    desde: ayerISO(), hasta: hoyISO(), q: '', // q client-side
  });
  const [datos, setDatos] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const s = await apiSucursales.listar(empresaId);
        setSucursales(Array.isArray(s) ? s : []);
      } catch { /* silent */ }
    })();
  }, [empresaId]);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    try {
      const r = await apiVentas.listar(empresaId, {
        sucursalId: filtros.sucursalId || undefined,
        estado: filtros.estado || undefined,
        desde: filtros.desde || undefined,
        hasta: filtros.hasta || undefined,
        page: 1, pageSize: 200,
      });
      // Heredado devuelve { data, totalCount, currentPage, pageSize }
      setDatos(Array.isArray(r?.data) ? r.data : []);
      setTotal(r?.totalCount ?? 0);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar ventas.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, filtros.sucursalId, filtros.estado, filtros.desde, filtros.hasta, notify]);

  useEffect(() => { cargar(); }, [cargar]);

  // Filtros client-side: estadoPago y busqueda por texto
  const datosFiltrados = useMemo(() => {
    let arr = datos;
    if (filtros.estadoPago) {
      arr = arr.filter(v => v.estadoPago === filtros.estadoPago);
    }
    if (filtros.q?.trim()) {
      const q = filtros.q.trim().toLowerCase();
      arr = arr.filter(v =>
        (v.numeroTicket || '').toLowerCase().includes(q)
        || (v.clienteNombre || '').toLowerCase().includes(q)
        || (v.clienteDocumento || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [datos, filtros.estadoPago, filtros.q]);

  const totales = useMemo(() => {
    const activas = datosFiltrados.filter(v => v.estado !== 'ANULADA');
    return {
      cantidad: activas.length,
      monto: activas.reduce((s, v) => s + Number(v.total || 0), 0),
      cobrado: activas
        .filter(v => v.estadoPago === 'PAGADO')
        .reduce((s, v) => s + Number(v.total || 0), 0),
      pendiente: activas
        .filter(v => v.estadoPago !== 'PAGADO')
        .reduce((s, v) => s + Number(v.total || 0), 0),
    };
  }, [datosFiltrados]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <ReceiptLongIcon color="primary" fontSize="large" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>Ventas</Typography>
          <Typography variant="body2" color="text.secondary">
            Historial y auditoría de ventas
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PointOfSaleIcon />}
                onClick={() => navigate('/pos')}>
          Nueva venta
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Historial" icon={<ReceiptLongIcon />} iconPosition="start" />
        <Tab label="Ingresos por canal" icon={<StorefrontIcon />} iconPosition="start" />
      </Tabs>

      {tab === 0 && <>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Ventas activas</Typography>
            <Typography variant="h5" fontWeight={700}>{totales.cantidad}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Total facturado</Typography>
            <Typography variant="h5" fontWeight={700}>{fmtCOP(totales.monto)}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Cobrado</Typography>
            <Typography variant="h5" fontWeight={700} color="success.main">
              {fmtCOP(totales.cobrado)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Pendiente / fiado</Typography>
            <Typography variant="h5" fontWeight={700} color="warning.main">
              {fmtCOP(totales.pendiente)}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
                     value={filtros.desde} onChange={(e) => setFiltros(f => ({ ...f, desde: e.target.value }))} />
          <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
                     value={filtros.hasta} onChange={(e) => setFiltros(f => ({ ...f, hasta: e.target.value }))} />
          <TextField select size="small" label="Sucursal" sx={{ minWidth: 160 }}
                     value={filtros.sucursalId}
                     onChange={(e) => setFiltros(f => ({ ...f, sucursalId: e.target.value }))}>
            <MenuItem value="">Todas</MenuItem>
            {sucursales.map(s => (
              <MenuItem key={s.sucursalId} value={s.sucursalId}>{s.nombre}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Estado" sx={{ minWidth: 130 }}
                     value={filtros.estado}
                     onChange={(e) => setFiltros(f => ({ ...f, estado: e.target.value }))}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="COMPLETADA">Completada</MenuItem>
            <MenuItem value="ANULADA">Anulada</MenuItem>
          </TextField>
          <TextField select size="small" label="Pago" sx={{ minWidth: 130 }}
                     value={filtros.estadoPago}
                     onChange={(e) => setFiltros(f => ({ ...f, estadoPago: e.target.value }))}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="PAGADO">Pagado</MenuItem>
            <MenuItem value="PARCIAL">Parcial</MenuItem>
            <MenuItem value="PENDIENTE">Pendiente</MenuItem>
          </TextField>
          <TextField size="small" placeholder="Ticket, cliente, documento"
                     sx={{ minWidth: 240, flexGrow: 1 }}
                     value={filtros.q}
                     onChange={(e) => setFiltros(f => ({ ...f, q: e.target.value }))} />
          <IconButton onClick={cargar}><RefreshIcon /></IconButton>
        </Stack>
      </Card>

      <Card>
        {cargando ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : datosFiltrados.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>Sin ventas con estos filtros.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Pago</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datosFiltrados.map(v => (
                  <TableRow key={v.ventaId} hover
                            sx={{ opacity: v.estado === 'ANULADA' ? 0.6 : 1 }}>
                    <TableCell><Typography variant="caption">{fmtFechaHora(v.fechaVenta)}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={500}>{v.numeroTicket}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">{v.clienteNombre || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{v.clienteDocumento || ''}</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={v.tipoVenta} variant="outlined" /></TableCell>
                    <TableCell>
                      <Chip size="small" label={v.estado}
                            color={v.estado === 'ANULADA' ? 'error' : 'default'} />
                    </TableCell>
                    <TableCell><Chip size="small" label={v.estadoPago} color={colorEstadoPago(v.estadoPago)} /></TableCell>
                    <TableCell align="right">
                      <b>{fmtCOP(v.totalRedondeado || v.total)}</b>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver detalle">
                        <IconButton size="small" onClick={() => setDialog({ tipo: 'detalle', ventaId: v.ventaId })}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {v.estado !== 'ANULADA' && (
                        <Tooltip title="Anular venta">
                          <IconButton size="small" color="error"
                                      onClick={() => setDialog({ tipo: 'anular', venta: v })}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {datosFiltrados.length > 0 && (
          <Box sx={{ p: 1.5, textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {datosFiltrados.length} de {total} ventas
            </Typography>
          </Box>
        )}
      </Card>

      </>}

      {tab === 1 && (
        <IngresosPorCanal empresaId={empresaId} sucursalId={filtros.sucursalId}
                          desde={filtros.desde} hasta={filtros.hasta} />
      )}

      {dialog?.tipo === 'detalle' && (
        <DialogDetalleVenta empresaId={empresaId} ventaId={dialog.ventaId}
                            onClose={() => setDialog(null)}
                            notify={notify} />
      )}
      {dialog?.tipo === 'anular' && (
        <DialogAnular venta={dialog.venta}
                      onClose={() => setDialog(null)}
                      onDone={async () => { setDialog(null); await cargar(); notify('Venta anulada.', 'success'); }}
                      onError={(m) => notify(m, 'error')} />
      )}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Detalle
// ══════════════════════════════════════════════════════════════════════

function DialogDetalleVenta({ empresaId, ventaId, onClose, notify }) {
  const [v, setV] = useState(null);
  const [cargando, setCargando] = useState(true);
  useEffect(() => {
    (async () => {
      try { setV(await apiVentas.obtener(empresaId, ventaId)); }
      catch (e) { notify(e.mensajeUsuario || 'No se pudo cargar.', 'error'); onClose(); }
      finally { setCargando(false); }
    })();
  }, [empresaId, ventaId, notify, onClose]);

  if (cargando || !v) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="md">
        <DialogContent><Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box></DialogContent>
      </Dialog>
    );
  }
  const detalles = v.detalles || [];
  const pagos = v.pagos || [];
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Venta {v.numeroTicket}
        <Typography variant="caption" display="block" color="text.secondary">
          {fmtFechaHora(v.fechaVenta)} · {v.sucursalNombre || '—'} · {v.tipoVenta}
          {v.cajaNombre ? ` · ${v.cajaNombre}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Cliente</Typography>
            <Typography variant="body2" fontWeight={500}>{v.clienteNombre || '—'}</Typography>
            <Typography variant="caption">{v.clienteDocumento || ''}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1}>
              <Chip size="small" label={v.estado} color={v.estado === 'ANULADA' ? 'error' : 'default'} />
              <Chip size="small" label={v.estadoPago} color={colorEstadoPago(v.estadoPago)} />
              {v.requiereFactura && <Chip size="small" label="Factura electrónica" color="info" />}
            </Stack>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" gutterBottom>Líneas</Typography>
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Desc.</TableCell>
                <TableCell align="right">IVA</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detalles.map(l => (
                <TableRow key={l.ventaDetalleId}>
                  <TableCell>
                    <Typography variant="body2">{l.nombreProducto}</Typography>
                    <Typography variant="caption" color="text.secondary">{l.codigoProducto}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {Number(l.cantidad).toLocaleString('es-CO')} {l.unidadAbreviatura || ''}
                  </TableCell>
                  <TableCell align="right">{fmtCOP(l.precioUnitario)}</TableCell>
                  <TableCell align="right">
                    {l.porcentajeDescuento > 0 ? `${l.porcentajeDescuento}%` : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption">{l.porcentajeIVA}%</Typography><br />
                    <Typography variant="caption" color="text.secondary">{fmtCOP(l.valorIVA)}</Typography>
                  </TableCell>
                  <TableCell align="right"><b>{fmtCOP(l.total)}</b></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="subtitle2" gutterBottom>Pagos</Typography>
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Método</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell align="right">Recibido</TableCell>
                <TableCell align="right">Cambio</TableCell>
                <TableCell>Referencia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagos.map(p => (
                <TableRow key={p.pagoVentaId}>
                  <TableCell>{p.metodoPagoNombre || `(id ${p.metodoPagoId})`}</TableCell>
                  <TableCell align="right"><b>{fmtCOP(p.monto)}</b></TableCell>
                  <TableCell align="right">{p.montoRecibido != null ? fmtCOP(p.montoRecibido) : '—'}</TableCell>
                  <TableCell align="right">{p.cambio != null ? fmtCOP(p.cambio) : '—'}</TableCell>
                  <TableCell>{p.referencia || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />
        <Stack alignItems="flex-end" spacing={0.5}>
          <Typography variant="body2">Subtotal: {fmtCOP(v.subtotal)}</Typography>
          {v.descuentoTotal > 0 && (
            <Typography variant="body2" color="text.secondary">
              Descuento: −{fmtCOP(v.descuentoTotal)}
            </Typography>
          )}
          <Typography variant="body2">IVA: {fmtCOP(v.totalIVA)}</Typography>
          <Typography variant="h5" fontWeight={700}>Total: {fmtCOP(v.totalRedondeado || v.total)}</Typography>
          {v.puntosGenerados > 0 && (
            <Typography variant="caption" color="success.main">
              +{v.puntosGenerados} puntos generados
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Anular
// ══════════════════════════════════════════════════════════════════════

function DialogAnular({ venta, onClose, onDone, onError }) {
  const [motivo, setMotivo] = useState('');
  const [g, setG] = useState(false);
  const motivoValido = motivo.trim().length >= 10;
  const submit = async () => {
    if (!motivoValido) return;
    setG(true);
    try { await apiVentas.anular(venta.ventaId, motivo.trim(), null); onDone(); }
    catch (e) { onError(e.mensajeUsuario || 'No se pudo anular.'); }
    finally { setG(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Anular venta {venta.numeroTicket}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Solo se pueden anular ventas del día actual y del turno de caja activo.
          Requiere rol Admin o Supervisor.
        </Alert>
        <TextField fullWidth required autoFocus multiline rows={3}
                   label="Motivo de anulación (mínimo 10 caracteres)"
                   value={motivo} onChange={(e) => setMotivo(e.target.value)}
                   helperText={`${motivo.trim().length} / 10 mínimo`}
                   error={motivo.length > 0 && !motivoValido} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" color="error" onClick={submit}
                disabled={!motivoValido || g}>
          {g ? 'Anulando…' : 'Anular venta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Ingresos por canal (POS vs HolaVeci)
// ══════════════════════════════════════════════════════════════════════

function IngresosPorCanal({ empresaId, sucursalId, desde, hasta }) {
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(false);
  useEffect(() => {
    if (!empresaId) return;
    (async () => {
      setCargando(true);
      try {
        const r = await apiVentas.resumenPorCanal(empresaId, {
          sucursalId: sucursalId || undefined,
          desde: desde || undefined,
          hasta: hasta || undefined,
        });
        setResumen(r);
      } catch { /* silent */ }
      finally { setCargando(false); }
    })();
  }, [empresaId, sucursalId, desde, hasta]);

  if (cargando || !resumen) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  const pos = resumen.pos;
  const hv = resumen.holaveci;
  const totalG = resumen.totalGeneral || 0;
  const pctPos = totalG > 0 ? Math.round((pos.total / totalG) * 100) : 0;
  const pctHv  = totalG > 0 ? Math.round((hv.total  / totalG) * 100) : 0;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Las ventas por HolaVeci son un canal separado — <b>no entran en la caja física</b>.
        Este resumen te permite verlas junto a las del POS mostrador.
      </Alert>

      <Grid container spacing={2}>
        {/* POS Mostrador */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderTop: 4, borderColor: 'primary.main' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <StorefrontIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h6" fontWeight={700}>POS Mostrador</Typography>
                <Typography variant="caption" color="text.secondary">
                  Ventas presenciales con turno de caja
                </Typography>
              </Box>
            </Stack>
            <Typography variant="h3" fontWeight={700} color="primary.main">{fmtCOP(pos.total)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {pctPos}% del total · {pos.numeroVentas} venta{pos.numeroVentas === 1 ? '' : 's'}
            </Typography>
            <LinearProgress variant="determinate" value={pctPos} sx={{ mb: 2, height: 8, borderRadius: 4 }} />
            <Stack direction="row" spacing={2}>
              <Paper variant="outlined" sx={{ p: 1.5, flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">Cobrado</Typography>
                <Typography variant="body1" fontWeight={700} color="success.main">{fmtCOP(pos.cobrado)}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">Pendiente</Typography>
                <Typography variant="body1" fontWeight={700} color="warning.main">{fmtCOP(pos.pendiente)}</Typography>
              </Paper>
            </Stack>
          </Card>
        </Grid>

        {/* HolaVeci */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderTop: 4, borderColor: 'success.main' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <LocalShippingIcon color="success" fontSize="large" />
              <Box>
                <Typography variant="h6" fontWeight={700}>HolaVeci</Typography>
                <Typography variant="caption" color="text.secondary">
                  Pedidos a domicilio del barrio
                </Typography>
              </Box>
            </Stack>
            <Typography variant="h3" fontWeight={700} color="success.main">{fmtCOP(hv.total)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {pctHv}% del total · {hv.numeroVentas} venta{hv.numeroVentas === 1 ? '' : 's'}
            </Typography>
            <LinearProgress variant="determinate" value={pctHv} color="success"
                            sx={{ mb: 2, height: 8, borderRadius: 4 }} />
            <Stack direction="row" spacing={2}>
              <Paper variant="outlined" sx={{ p: 1.5, flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">Cobrado</Typography>
                <Typography variant="body1" fontWeight={700} color="success.main">{fmtCOP(hv.cobrado)}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">Pendiente</Typography>
                <Typography variant="body1" fontWeight={700} color="warning.main">{fmtCOP(hv.pendiente)}</Typography>
              </Paper>
            </Stack>
          </Card>
        </Grid>

        {/* Total */}
        <Grid item xs={12}>
          <Card sx={{ p: 3, background: 'linear-gradient(90deg,#e8f5e9,#e3f2fd)' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">Total combinado del período</Typography>
                <Typography variant="h3" fontWeight={700}>{fmtCOP(totalG)}</Typography>
              </Box>
              <Stack spacing={0.5} sx={{ textAlign: 'right' }}>
                <Typography variant="caption">
                  POS: <b>{fmtCOP(pos.total)}</b> ({pctPos}%)
                </Typography>
                <Typography variant="caption">
                  HolaVeci: <b>{fmtCOP(hv.total)}</b> ({pctHv}%)
                </Typography>
                <Typography variant="caption">
                  {(pos.numeroVentas + hv.numeroVentas)} ventas totales
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Grid>

        {/* Serie diaria */}
        {resumen.serie?.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6">Evolución diaria</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Canal</TableCell>
                      <TableCell align="right">Ventas</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...resumen.serie].reverse().map((s, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          {new Date(s.fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}
                        </TableCell>
                        <TableCell>
                          <Chip size="small"
                                label={s.canal === 'POS' ? 'Local' : 'HolaVeci'}
                                color={s.canal === 'POS' ? 'primary' : 'success'} />
                        </TableCell>
                        <TableCell align="right">{s.numeroVentas}</TableCell>
                        <TableCell align="right"><b>{fmtCOP(s.total)}</b></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default Ventas;
